import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { localize } from '@saviynt/common';
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@saviynt/design-system';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import {
  CAMPAIGN_TYPES,
  CAMPAIGNTYPE_ENTITLEMENTOWNER,
} from '../../../../utils/constant';
import { getMessageKey, scrollToPosition } from '../../../../utils/utils';
import {
  DecisionLabel,
  TypeLabel,
} from '../../../CertsDetails/PopoverProgressBreakdown';

import {
  DECISION_DONE,
  DECISION_ICONS,
  DECISION_PENDING,
  DECISION_PROGRESS,
  DECISION_TITLES_AND_ORDER,
  DECISION_TOTAL,
  EO_TYPE_TITLES_AND_ORDER,
  STEP1_CATEGORY_DECISION,
  STEP1_CATEGORY_TYPE,
  TRANSITION_TIMEOUT,
  TYPE_ICONS,
  TYPE_TITLES_AND_ORDER,
} from './constant';
import CONFIGDATA from './mockData';

import './Step1.css';

function Step1({ isModalOpen, firstStep, configData }) {
  const [selection, setSelection] = useState(1);
  const [step1DataByDecision, setStep1DataByDecision] = useState({});
  const [step1DataByType, setStep1DataByType] = useState({});
  const intl = useIntl();

  const {
    core: { campaignType },
  } = useSelector((state) => state?.certifications?.details);
  const { messages } = useSelector((state) => state?.user);
  const isEOCampaign =
    CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_ENTITLEMENTOWNER;
  const step1BodyWrapperRef = useRef('step1BodyWrapperRef');

  const shouldRenderDecisionList = Boolean(
    step1DataByDecision && Object.keys(step1DataByDecision).length > 0
  );
  const shouldRenderTypeList = Boolean(
    step1DataByType && Object.keys(step1DataByType).length > 0
  );
  const shouldShowNoDataForDecision = Boolean(
    configData?.todoTabCount === 0 && !shouldRenderDecisionList
  );

  const TYPE_TITLES = {
    Role:
      messages['Role_DISPLAYNAME.CERTIFICATIONS'] ||
      localize(intl, getMessageKey('Role_DISPLAYNAME.CERTIFICATIONS')),
    Account:
      messages['Account_DISPLAYNAME.CERTIFICATIONS'] ||
      localize(intl, getMessageKey('Account_DISPLAYNAME.CERTIFICATIONS')),
    User:
      messages['User_DISPLAYNAME.CERTIFICATIONS'] ||
      localize(intl, getMessageKey('User_DISPLAYNAME.CERTIFICATIONS')),
    Entitlement:
      messages['Ent_DISPLAYNAME.CERTIFICATIONS'] ||
      localize(intl, getMessageKey('Ent_DISPLAYNAME.CERTIFICATIONS')),
    ChildEntitlement:
      messages['ChildEnt_DISPLAYNAME.CERTIFICATIONS'] ||
      localize(intl, getMessageKey('ChildEnt_DISPLAYNAME.CERTIFICATIONS')),
    ParentEnt:
      messages['Ent_DISPLAYNAME.CERTIFICATIONS'] ||
      localize(intl, getMessageKey('Ent_DISPLAYNAME.CERTIFICATIONS')),
    allitems:
      messages.CERTS_EXPORT_WHAT_TO_EXPORT_ALL_ITEMS ||
      localize(intl, getMessageKey('CERTS_EXPORT_WHAT_TO_EXPORT_ALL_ITEMS')), //
    default:
      messages.CERTS_NOT_PROVIDED ||
      localize(intl, getMessageKey('CERTS_NOT_PROVIDED')),
  };

  const PROGRESS_LABELS = {
    Done: 'Done.v2.label.ATTESTATIONS',
    Progress: 'Progress.v2.label.ATTESTATIONS',
    'End Date Changed': 'EndDateChanged.v2.label.ATTESTATIONS',
    Total: 'Total.v2.label.ATTESTATIONS',
    Approved: 'Approved.v2.label.ATTESTATIONS',
    'Manager Confirmed': 'ManagerConfirmed.v2.label.ATTESTATIONS',
    Rejected: 'Rejected.v2.label.ATTESTATIONS',
    Terminated: 'Terminated.v2.label.ATTESTATIONS',
    Pending: 'Pending.v2.label.ATTESTATIONS',
    'Manager Confirmed With End Date Changed':
      'ManagerConfirmedWithEndDateChanged.v2.label.ATTESTATIONS',
    ManagerConfirmed: 'ManagerConfirmed.v2.label.ATTESTATIONS',
    ManagerConfirmedWithEndDateChanged:
      'ManagerConfirmedWithEndDateChanged.v2.label.ATTESTATIONS',
    EndDateChanged: 'EndDateChanged.v2.label.ATTESTATIONS',
    NotConfirmedByManager: 'NotConfirmedByManager.v2.label.ATTESTATIONS',
    'Not Confirmed By Manager': 'NotConfirmedByManager.v2.label.ATTESTATIONS',
    ConditionallyCertified: 'ConditionallyCertified.v2.label.ATTESTATIONS',
    'Conditionally Certified': 'ConditionallyCertified.v2.label.ATTESTATIONS',
    'Does Not Belong To Me': 'DoesNotBelongToMe.v2.label.ATTESTATIONS',
    'Does Not Belong': 'DoesNotBelong.v2.label.ATTESTATIONS',
    Decommissioned: 'Decommissioned.v2.label.ATTESTATIONS',
    NoActionRequired: 'NoActionRequired.v2.label.ATTESTATIONS',
  };

  const viewToDoListObjects = [
    DECISION_PENDING,
    DECISION_PROGRESS,
    DECISION_DONE,
    DECISION_TOTAL,
  ];

  useEffect(() => {
    setTimeout(() => {
      setSelection(1);
      scrollToPosition(step1BodyWrapperRef, 0);
    }, TRANSITION_TIMEOUT);
  }, [firstStep, isModalOpen]);

  useEffect(() => {
    if (configData) {
      setStep1DataByDecision(configData?.step1Data?.[STEP1_CATEGORY_DECISION]);
      setStep1DataByType(configData?.step1Data?.[STEP1_CATEGORY_TYPE]);
    }
  }, [configData]);

  const focusOnlyModalIslandElements = (e) => {
    if (!step1BodyWrapperRef.current) return;

    const focusableElements = step1BodyWrapperRef.current.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (e.keyCode === 9) {
      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          lastElement.focus();
          e.preventDefault();
        }
      } else if (document.activeElement === lastElement) {
        firstElement.focus();
        e.preventDefault();
      }
    }
  };

  useEffect(() => {
    if (firstStep) {
      document.body.classList.add('Carousel--isOpen--bodyScrollLock');
      document.addEventListener('keydown', focusOnlyModalIslandElements);
      const closeButton = document.getElementById('modalCloseButton-true');

      closeButton?.focus();
    } else {
      document.body.classList.remove('Carousel--isOpen--bodyScrollLock');
    }

    return () => {
      document.body.classList.remove('Carousel--isOpen--bodyScrollLock');
      document.removeEventListener('keydown', focusOnlyModalIslandElements);
    };
  }, [firstStep]);

  const reviewAndSubmitModalBodyStep1 = classnames(
    'ReviewAndSubmitModal-bodyStep1Wrapper'
  );

  const renderStep1Shorts = () => (
    <div className='ReviewAndSubmitModal-bodyStep1WrapperShorts'>
      <TypeLabel
        count={step1DataByDecision?.[DECISION_DONE] || 0}
        totalCount={step1DataByDecision?.[DECISION_TOTAL] || 0}
        label={
          messages.CERTS_EXPORT_WHAT_TO_EXPORT_ALL_ITEMS ||
          localize(intl, getMessageKey('CERTS_EXPORT_WHAT_TO_EXPORT_ALL_ITEMS'))
        }
        doneLabel={
          messages.CERTS_DONE || localize(intl, getMessageKey('CERTS_DONE'))
        }
        ofLabel={
          messages.CERTS_OF_LABEL ||
          localize(intl, getMessageKey('CERTS_OF_LABEL'))
        }
      />
    </div>
  );

  const renderStep1Stats = () => (
    <div className='ReviewAndSubmitModal-bodyStep1WrapperStats'>
      <Typography
        kind='body2-bold'
        className='ReviewAndSubmitModal-bodyStep1WrapperStatsTitle'>
        {messages.CERTS_PROGRESS_DETAILS ||
          localize(intl, getMessageKey('CERTS_PROGRESS_DETAILS'))}
      </Typography>
      <Box className='ReviewAndSubmitModal-bodyStep1WrapperStatsDescription'>
        <ToggleButtonGroup selection={selection} setSelection={setSelection}>
          <ToggleButton
            label={
              messages.CERTS_BY_DECISION ||
              localize(intl, getMessageKey('CERTS_BY_DECISION'))
            }
          />
          <ToggleButton
            label={
              messages.CERTS_BY_TYPE ||
              localize(intl, getMessageKey('CERTS_BY_TYPE'))
            }
          />
        </ToggleButtonGroup>
        {!configData?.isComplete && (
          <>
            <div className='ReviewAndSubmitModal-bodyStep1WrapperStatsDescriptionIncomplete'>
              <Typography
                kind='body1'
                className='ReviewAndSubmitModal-bodyStep1WrapperStatsDescriptionIncompleteText'>
                {`${configData?.todoTabCount}`}
              </Typography>
              <Typography
                kind='body1'
                className='ReviewAndSubmitModal-bodyStep1WrapperStatsDescriptionIncompleteText'>
                {messages.CERTS_TODO ||
                  localize(intl, getMessageKey('CERTS_TODO'))}
              </Typography>
            </div>
            <span className='ReviewAndSubmitModal-bodyStep1WrapperHLine' />
            <div className='ReviewAndSubmitModal-bodyStep1WrapperStatsDescriptionIncomplete'>
              <Typography
                kind='body1'
                className='ReviewAndSubmitModal-bodyStep1WrapperStatsDescriptionIncompleteText'>
                {`${configData?.doneTabCount}`}
              </Typography>
              <Typography
                kind='body1'
                className='ReviewAndSubmitModal-bodyStep1WrapperStatsDescriptionIncompleteText'>
                {messages.CERTS_DONE ||
                  localize(intl, getMessageKey('CERTS_DONE'))}
              </Typography>
            </div>
          </>
        )}
        <div className='ReviewAndSubmitModal-bodyStep1WrapperStatsDescriptionList'>
          <div
            className={
              selection === 1
                ? 'ReviewAndSubmitModal-bodyStep1WrapperStatsDescriptionDecisionList--visible'
                : 'ReviewAndSubmitModal-bodyStep1WrapperStatsDescriptionDecisionList--hidden'
            }>
            {shouldRenderDecisionList &&
              Object.keys(step1DataByDecision)
                .filter((key) => !viewToDoListObjects.includes(key))
                .sort(
                  (a, b) =>
                    DECISION_TITLES_AND_ORDER[a] - DECISION_TITLES_AND_ORDER[b]
                )
                .map((key) => (
                  <DecisionLabel
                    key={key}
                    value={step1DataByDecision[key] || 0}
                    iconKind={DECISION_ICONS[key] || 'Terminate'}
                    label={
                      messages[PROGRESS_LABELS[key]] ||
                      localize(intl, getMessageKey(PROGRESS_LABELS[key])) ||
                      'Terminated'
                    }
                  />
                ))}
            {shouldShowNoDataForDecision && (
              <Typography
                kind='body2'
                className='ReviewAndSubmitModal-bodyStep1WrapperStatsDescriptionListCategoryMessage'>
                {messages.CERTS_STEP1_NO_DATE_MSG ||
                  localize(intl, getMessageKey('CERTS_STEP1_NO_DATE_MSG'))}
              </Typography>
            )}
          </div>
          <div
            className={
              selection === 2
                ? 'ReviewAndSubmitModal-bodyStep1WrapperStatsDescriptionTypeList--visible'
                : 'ReviewAndSubmitModal-bodyStep1WrapperStatsDescriptionTypeList--hidden'
            }>
            {shouldRenderTypeList &&
              Object.keys(
                isEOCampaign ? EO_TYPE_TITLES_AND_ORDER : TYPE_TITLES_AND_ORDER
              )
                .filter((key) => Boolean(step1DataByType[key]))
                .map((key) => (
                  <TypeLabel
                    key={key}
                    count={step1DataByType?.[key]?.[DECISION_DONE]}
                    totalCount={step1DataByType?.[key]?.[DECISION_TOTAL]}
                    label={TYPE_TITLES[key] || ''}
                    iconKind={TYPE_ICONS?.[key] || ''}
                    doneLabel={
                      messages.CERTS_DONE ||
                      localize(intl, getMessageKey('CERTS_DONE'))
                    }
                    ofLabel={
                      messages.CERTS_OF_LABEL ||
                      localize(intl, getMessageKey('CERTS_OF_LABEL'))
                    }
                  />
                ))}
            {!shouldRenderTypeList && (
              <Typography
                kind='body2'
                className='ReviewAndSubmitModal-bodyStep1WrapperStatsDescriptionListCategoryMessage'>
                {messages.CERTS_STEP1_NO_DATE_MSG ||
                  localize(intl, getMessageKey('CERTS_STEP1_NO_DATE_MSG'))}
              </Typography>
            )}
          </div>
        </div>
      </Box>
    </div>
  );

  return (
    <div className={reviewAndSubmitModalBodyStep1} ref={step1BodyWrapperRef}>
      {renderStep1Shorts()}
      {renderStep1Stats()}
    </div>
  );
}

Step1.propTypes = {
  isModalOpen: PropTypes.bool.isRequired,
  firstStep: PropTypes.bool.isRequired,
  configData: PropTypes.shape(CONFIGDATA),
};

Step1.defaultProps = { configData: {} };

export default Step1;
